<script setup lang="ts">
  import {
    Button,
    Switch,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription
  } from '@gat-ship/vue-components'
  import { IconCopy, IconDatabase } from '@gat-ship/icons'
  import { Badge } from '@gat-ship/vue-components'
  import { globalUtils } from '@gat-ship/utils'
  import { useAuth0 } from '@gat-ship/auth'
  import { useAccountSettings } from '../composables/useAccountSettings'

  const { logout } = useAuth0()
  const {
    user,
    effectiveDefaultDatabaseId,
    setDefaultDatabase,
    showDevInfo,
    grantedFlags,
    getFlagMeta,
    isFeatureFlagEnabled,
    toggleFeatureFlag
  } = useAccountSettings()
</script>

<template>
  <div v-if="user.userProfile.value" class="flex flex-col gap-5">
    <ModalSection>
      <ModalSectionHeader class="pt-1">
        <ModalSectionHeaderTitle>Account</ModalSectionHeaderTitle>
      </ModalSectionHeader>
      <div class="gap-0 divide-y">
        <div class="flex items-center justify-between py-3">
          <span class="text-sm font-medium"> Log out </span>
          <div class="flex items-center gap-2">
            <Button variant="outline" @click="logout()">Log out</Button>
          </div>
        </div>
        <template v-if="showDevInfo">
          <div class="flex items-center justify-between py-3">
            <span class="text-sm font-medium"> User ID </span>
            <div class="flex items-center gap-2">
              <span
                class="rounded-sm bg-gray-100 px-2 py-1 text-xs text-gray-500"
              >
                {{ user.userProfile.value.userId }}
              </span>
              <Button
                variant="ghost"
                size="icon"
                @click="
                  globalUtils.functions.copyToClipboard(
                    user.userProfile.value.userId
                  )
                "
              >
                <IconCopy />
              </Button>
            </div>
          </div>
          <div class="flex items-center justify-between py-3">
            <span class="text-sm font-medium"> Tenant ID </span>
            <div class="flex items-center gap-2">
              <span
                class="rounded-sm bg-gray-100 px-2 py-1 text-xs text-gray-500"
              >
                {{ user.userProfile.value.homeTenantId }}
              </span>
              <Button
                variant="ghost"
                size="icon"
                @click="
                  globalUtils.functions.copyToClipboard(
                    user.userProfile.value.homeTenantId
                  )
                "
              >
                <IconCopy />
              </Button>
            </div>
          </div>
          <div class="flex items-center justify-between py-3">
            <span class="text-sm font-medium"> Database ID </span>
            <div class="flex items-center gap-2">
              <span
                class="rounded-sm bg-gray-100 px-2 py-1 text-xs text-gray-500"
              >
                {{ user.databases.value.current?.id }}
              </span>
              <Button
                variant="ghost"
                size="icon"
                @click="
                  globalUtils.functions.copyToClipboard(
                    user.databases.value.current?.id ?? ''
                  )
                "
              >
                <IconCopy />
              </Button>
            </div>
          </div>
        </template>
      </div>
    </ModalSection>
    <ModalSection v-if="user.databases.value.all.length > 1">
      <ModalSectionHeader>
        <ModalSectionHeaderTitle>Databases</ModalSectionHeaderTitle>
        <ModalSectionHeaderDescription>
          Switch between databases you have access to.
        </ModalSectionHeaderDescription>
      </ModalSectionHeader>
      <div class="flex flex-col divide-y">
        <div
          v-for="db in user.databases.value.all"
          :key="db.id"
          class="flex items-center justify-between gap-4 py-3"
        >
          <div class="flex items-center gap-2">
            <IconDatabase class="text-muted-foreground size-4 shrink-0" />
            <div class="flex flex-col items-start gap-1">
              <span class="text-sm font-medium">{{ db.name }}</span>
              <Badge
                v-if="db.id === effectiveDefaultDatabaseId"
                variant="secondary"
              >
                Startup
              </Badge>
              <Button
                v-else
                variant="link"
                size="sm"
                class="h-auto p-0"
                @click="setDefaultDatabase(db.id)"
              >
                Use on startup
              </Button>
            </div>
          </div>
          <div
            v-if="db.id === user.databases.value.current?.id"
            class="flex items-center gap-1.5 text-green-600"
          >
            <span class="size-2 rounded-full bg-green-500" />
            <span class="text-xs font-medium">Current</span>
          </div>
          <Button
            v-else
            variant="outline"
            size="sm"
            @click="user.switchDatabase(db.id)"
          >
            Switch to this
          </Button>
        </div>
      </div>
    </ModalSection>
    <ModalSection v-if="showDevInfo && grantedFlags.length > 0">
      <ModalSectionHeader>
        <ModalSectionHeaderTitle>Feature Flags</ModalSectionHeaderTitle>
        <ModalSectionHeaderDescription>
          Toggle feature flags granted to this environment.
        </ModalSectionHeaderDescription>
      </ModalSectionHeader>
      <div class="flex flex-col divide-y">
        <div
          v-for="{ name } in grantedFlags"
          :key="name"
          class="flex items-center justify-between gap-4 py-3"
        >
          <div class="flex flex-col">
            <span class="text-sm font-medium">
              {{ getFlagMeta(name).title }}
            </span>
            <span
              v-if="getFlagMeta(name).description"
              class="text-muted-foreground text-xs"
            >
              {{ getFlagMeta(name).description }}
            </span>
          </div>
          <Switch
            :model-value="isFeatureFlagEnabled(name)"
            @update:model-value="toggleFeatureFlag(name, $event)"
          />
        </div>
      </div>
    </ModalSection>
  </div>
</template>
